<template>
    <BaseDialog
        v-model:open="open"
        :title="dialogTitle"
        :description="dialogDescription"
        layout="custom"
        width="default"
        :show-close="!busy"
        float-close
        mobile-height="expanded"
        dialog-content-class="fs-mask flex flex-col gap-6 p-6 overflow-hidden"
        :disable-close-on-backdrop-click="busy"
        :reserve-mobile-nav-space="false">
        <template #trigger
            ><BaseButton theme="primary" :disabled="disabled"><i class="bi bi-people mr-2" aria-hidden="true" />{{ t("teamPage.manage") }}</BaseButton></template
        >
        <template v-if="view.kind !== 'members'">
            <div
                ref="confirmationRegion"
                tabindex="0"
                role="region"
                :aria-label="dialogTitle"
                :aria-describedby="confirmationDescriptionId"
                class="flex min-h-0 flex-col gap-6 overflow-y-auto overscroll-contain break-words">
                <header class="pr-10">
                    <h2 class="display-small-serif break-words text-ink">{{ dialogTitle }}</h2>
                </header>
                <p v-if="view.kind === 'confirm-delete'" :id="confirmationDescriptionId" class="body text-ink">
                    <strong>{{ t("teamPage.delete.consequence") }}</strong>
                    {{ t("teamPage.delete.description") }}
                    <strong>{{ t("teamPage.delete.irreversible") }}</strong>
                </p>
                <p v-else :id="confirmationDescriptionId" class="body text-ink">{{ dialogDescription }}</p>
                <BaseNotification v-if="error" theme="error" :text="error" hide-dismiss />
            </div>
            <footer class="mt-auto flex shrink-0 flex-wrap justify-end gap-3">
                <BaseButton theme="neutral" :disabled="busy" @click="showMembers">{{ t("teamPerspective.cancel") }}</BaseButton>
                <BaseButton :theme="view.kind === 'confirm-make-lead' ? 'primary' : 'danger'" :disabled="busy" @click="confirmAction">{{ confirmationAction }}</BaseButton>
            </footer>
        </template>
        <template v-else>
            <div class="flex min-h-0 flex-col gap-6 overflow-y-auto overscroll-contain break-words">
                <header class="pr-10">
                    <p class="scaffold-details-small text-ink-muted">{{ t("teamPage.manage") }}</p>
                    <h2 class="display-small-serif break-words text-ink">{{ report.name }}</h2>
                </header>
                <section v-if="report.add_members_url && report.member_search_url" class="flex shrink-0 flex-col gap-3" :aria-label="t('teamPage.addMembers')">
                    <FormField id="add-team-member-search" :label="t('peopleDirectory.createTeam.searchLabel')" :hint="t('peopleDirectory.createTeam.searchHint')">
                        <BaseCombobox
                            v-model="candidate"
                            v-model:input="query"
                            :clearable="false"
                            :ignore-filter="true"
                            :options="options"
                            :key-value="(person) => person.id"
                            :display-value="(person) => `${person.name} ${person.email}`"
                            :placeholder="t('peopleDirectory.createTeam.searchPlaceholder')"
                            :disabled="busy || selected.length >= 100"
                            @keydown.enter.prevent>
                            <template #item="{ option }">
                                <div class="min-w-0 fs-mask">
                                    <p class="body-small-semibold text-ink truncate">{{ option.name }}</p>
                                    <p class="details text-ink-muted truncate">{{ option.email }}</p>
                                </div>
                            </template>
                            <template #empty
                                ><p class="body-small p-3 text-ink-muted">{{ searchStatus }}</p></template
                            >
                        </BaseCombobox>
                    </FormField>
                    <p class="details text-ink-muted empty:sr-only" role="status">{{ searchStatus }}</p>
                    <div class="empty:sr-only" role="alert">
                        <template v-if="searchError">
                            <p class="body-small text-ink">{{ t("peopleDirectory.createTeam.searchError") }}</p>
                            <BaseButton theme="secondary" size="sm" :disabled="busy || searching" @click="searchRequest?.execute()">{{ t("peopleDirectory.retry") }}</BaseButton>
                        </template>
                    </div>
                    <ul v-if="selected.length" class="max-h-32 overflow-y-auto">
                        <li v-for="person in selected" :key="person.id" class="flex items-center justify-between gap-3 py-1">
                            <span class="body-small text-ink break-words">{{ person.name }}</span>
                            <BaseButton
                                theme="neutral"
                                size="sm"
                                :disabled="busy"
                                :aria-label="t('peopleDirectory.createTeam.remove', { name: person.name })"
                                @click="selected = selected.filter((member) => member.id !== person.id)"
                                >{{ t("teamPage.remove") }}</BaseButton
                            >
                        </li>
                    </ul>
                    <BaseButton theme="primary" class="self-start" :disabled="busy || !selected.length" @click="addMembers">{{
                        busy ? t("teamPage.saving") : t("teamPage.addMembers")
                    }}</BaseButton>
                    <div ref="addFeedback">
                        <div role="status">
                            <p v-if="addedCount" class="body-small text-ink">{{ t("teamPage.added", { count: addedCount }) }}</p>
                        </div>
                        <BaseNotification theme="error" hide-dismiss class="empty:sr-only">
                            <template #content>
                                <p v-if="addError" class="body-small text-ink">{{ addError }}</p>
                            </template>
                        </BaseNotification>
                    </div>
                </section>
                <div class="flex flex-col gap-2">
                    <div class="flex items-center justify-between gap-3">
                        <h3 class="body-small-semibold text-ink">{{ t("teamPage.members") }}</h3>
                        <span class="details text-ink-muted">{{ t("teamPage.memberCount", { count: report.members.length }) }}</span>
                    </div>
                    <ul class="rounded-lgr border border-border px-4 py-2">
                        <li v-for="member in report.members" :key="member.id" class="flex items-center gap-3 py-2">
                            <PerspectiveAvatar :name="member.name" :letters="member.result?.letters" size="sm" aria-hidden="true" />
                            <div class="min-w-0 flex-1">
                                <div class="flex flex-wrap items-center gap-2">
                                    <p class="body-serif font-semibold break-words text-ink">{{ member.name }}</p>
                                    <BaseChip v-if="member.role === 'lead' || member.role === 'admin'" size="2xs">{{
                                        member.role === "lead" ? t("teamPage.lead") : t("teamPage.admin")
                                    }}</BaseChip>
                                </div>
                                <p v-if="member.job_title" class="details text-ink-muted">{{ member.job_title }}</p>
                            </div>
                            <TeamLeaveAction
                                v-if="member.id === report.viewer_id && leaveUrl"
                                :can-leave="report.can_leave"
                                :team-name="report.name"
                                :leave-url="leaveUrl"
                                :disabled="busy"
                                @left="emit('left')" />
                            <BaseDropdownMenu
                                v-else-if="member.remove_url || member.make_lead_url"
                                :sections="memberActions(member)"
                                @select="selectMemberAction(member, $event)"
                                @close-auto-focus="keepConfirmationFocus">
                                <template #trigger>
                                    <BaseButton
                                        ref="memberButtons"
                                        :data-member-id="member.id"
                                        theme="tertiary"
                                        size="sm"
                                        icon-only
                                        :disabled="busy"
                                        :aria-label="t('teamPage.memberOptions', { name: member.name })">
                                        <i class="bi bi-three-dots" aria-hidden="true" />
                                    </BaseButton>
                                </template>
                            </BaseDropdownMenu>
                        </li>
                    </ul>
                    <p class="details text-ink-muted">{{ report.can_manage ? t("teamPage.removeHelp") : t("teamPage.memberHelp") }}</p>
                </div>
            </div>
            <footer class="mt-auto flex shrink-0 flex-col gap-3">
                <div role="status">
                    <p v-if="successMessage" class="body-small text-ink">{{ successMessage }}</p>
                </div>
                <BaseNotification v-if="error" theme="error" :text="error" hide-dismiss />
                <div class="flex flex-wrap items-center justify-between gap-3">
                    <TeamLeaveAction
                        v-if="leaveUrl && !viewerInRoster"
                        :can-leave="report.can_leave"
                        :team-name="report.name"
                        :leave-url="leaveUrl"
                        :disabled="busy"
                        @left="emit('left')" />
                    <BaseButton
                        v-if="deleteUrl && report.can_manage"
                        ref="deleteButton"
                        theme="tertiary"
                        size="sm"
                        class="body-small min-h-6 px-0 py-0 text-button-danger hover:bg-transparent hover:text-button-danger-hover hover:underline disabled:text-button-disabled-text"
                        :disabled="busy"
                        @click="confirmDelete"
                        >{{ t("teamPage.delete.action") }}</BaseButton
                    >
                    <BaseButton ref="doneButton" theme="primary" class="ml-auto" :disabled="busy" @click="open = false">{{ t("teamPage.done") }}</BaseButton>
                </div>
            </footer>
        </template>
    </BaseDialog>
</template>
<script setup lang="ts">
import { refDebounced, type UseFetchReturn } from "@vueuse/core";
import { useFetch } from "~vue/api";
import type { TeamAddMembersRequest, TeamCandidate, TeamCandidateSearch, TeamPerspectiveMember, TeamPerspectiveReport } from "~vue/api/generated/team-perspective";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseChip from "~vue/components/BaseChip.vue";
import BaseCombobox from "~vue/components/BaseCombobox.vue";
import BaseDialog from "~vue/components/BaseDialog.vue";
import BaseDropdownMenu, { type DropdownMenuItem, type DropdownMenuSection } from "~vue/components/BaseDropdownMenu.vue";
import BaseNotification from "~vue/components/BaseNotification.vue";
import FormField from "~vue/components/form/FormField.vue";
import PerspectiveAvatar from "~vue/components/PerspectiveAvatar.vue";
import TeamLeaveAction from "~vue/Perspective/TeamLeaveAction.vue";
import { computed, nextTick, ref, shallowRef, useId, useTemplateRef, watch } from "vue";
import { useI18n } from "vue-i18n";

const { report, leaveUrl, deleteUrl, disabled = false } = defineProps<{ report: TeamPerspectiveReport; leaveUrl: string | null; deleteUrl: string | null; disabled?: boolean }>();
const emit = defineEmits<{ updated: [report: TeamPerspectiveReport]; left: []; deleted: [] }>();
const { t } = useI18n();
const open = ref(false);
const viewerInRoster = computed(() => report.members.some((member) => member.id === report.viewer_id));
const confirmationRegion = useTemplateRef<HTMLDivElement>("confirmationRegion");
const confirmationDescriptionId = useId();
const memberButtons = useTemplateRef<InstanceType<typeof BaseButton>[]>("memberButtons");
const doneButton = useTemplateRef<InstanceType<typeof BaseButton>>("doneButton");
const deleteButton = useTemplateRef<InstanceType<typeof BaseButton>>("deleteButton");
type View =
    | { kind: "members" }
    | { kind: "confirm-delete" }
    | { kind: "confirm-remove"; member: TeamPerspectiveMember }
    | { kind: "confirm-make-lead"; member: TeamPerspectiveMember };
const view = ref<View>({ kind: "members" });
const dialogTitle = computed(() => {
    if (view.value.kind === "confirm-make-lead") {
        return t("teamPage.makeLead.title", { name: view.value.member.name });
    }
    if (view.value.kind === "confirm-remove") {
        return t("teamPage.removeConfirmation.title", { name: view.value.member.name });
    }
    return view.value.kind === "confirm-delete" ? t("teamPage.delete.title", { name: report.name }) : report.name;
});
const dialogDescription = computed(() => {
    if (view.value.kind === "confirm-make-lead") {
        return t("teamPage.makeLead.description", { name: view.value.member.name, team: report.name });
    }
    if (view.value.kind === "confirm-remove") {
        return t("teamPage.removeConfirmation.description", { name: view.value.member.name, team: report.name });
    }
    return view.value.kind === "confirm-delete"
        ? [t("teamPage.delete.consequence"), t("teamPage.delete.description"), t("teamPage.delete.irreversible")].join(" ")
        : t("teamPage.manage");
});
const confirmationAction = computed(() => {
    if (view.value.kind === "confirm-make-lead") {
        return busy.value ? t("teamPage.makeLead.pending") : t("teamPage.makeLead.action");
    }
    if (view.value.kind === "confirm-remove") {
        return busy.value ? t("teamPage.removeConfirmation.pending") : t("teamPage.remove");
    }
    return busy.value ? t("teamPage.delete.pending") : t("teamPage.delete.action");
});
const pending = ref<"remove-member" | "delete-team" | "make-lead" | "add-members" | null>(null);
const busy = computed(() => pending.value !== null);
const error = ref("");
const successMessage = ref("");
const addError = ref("");
const addFeedback = useTemplateRef<HTMLDivElement>("addFeedback");
const addedCount = ref(0);
const selected = ref<TeamCandidate[]>([]);
const candidate = ref<TeamCandidate | null>(null);
const query = ref("");
const debouncedQuery = refDebounced(query, 250);
const searchRequest = shallowRef<UseFetchReturn<TeamCandidateSearch>>();
const searchUrl = computed(() =>
    open.value && view.value.kind === "members" && report.member_search_url && query.value === debouncedQuery.value && debouncedQuery.value.trim().length >= 2
        ? `${report.member_search_url}?${new URLSearchParams({ q: debouncedQuery.value.trim() })}`
        : null,
);
watch(searchUrl, (url, _old, onCleanup) => {
    searchRequest.value = undefined;
    if (!url) {
        return;
    }
    const request = useFetch(url, { immediate: false }).get().json<TeamCandidateSearch>();
    searchRequest.value = request;
    onCleanup(() => request.abort());
    void request.execute();
});
const searchError = computed(() => searchRequest.value?.error.value);
const searching = computed(() => query.value !== debouncedQuery.value || (searchRequest.value?.isFetching.value ?? false));
const options = computed(() => {
    if (searching.value || searchError.value || query.value.trim().length < 2) {
        return [];
    }
    const excluded = new Set([...report.members.map((member) => member.id), ...selected.value.map((member) => member.id)]);
    return (searchRequest.value?.data.value?.results ?? []).filter((person) => !excluded.has(person.id));
});
const searchStatus = computed(() => {
    if (selected.value.length >= 100) {
        return t("peopleDirectory.createTeam.limit");
    }
    if (query.value.trim().length < 2 || searchError.value) {
        return "";
    }
    if (searching.value) {
        return t("peopleDirectory.createTeam.searching");
    }
    return options.value.length ? t("peopleDirectory.createTeam.results", { count: options.value.length }) : t("peopleDirectory.createTeam.noMatches");
});
watch(candidate, async (person) => {
    if (!person || busy.value || selected.value.length >= 100) {
        return;
    }
    if (!selected.value.some((member) => member.id === person.id)) {
        selected.value.push(person);
    }
    await nextTick();
    candidate.value = null;
    query.value = "";
});
async function addMembers() {
    if (!report.add_members_url || busy.value || !selected.value.length) {
        return;
    }
    pending.value = "add-members";
    error.value = "";
    addError.value = "";
    successMessage.value = "";
    addedCount.value = 0;
    const payload: TeamAddMembersRequest = { member_ids: selected.value.map((person) => person.id) };
    try {
        const { data, error: failure } = await useFetch(report.add_members_url).post(payload).json<TeamPerspectiveReport>();
        if (failure.value || !data.value) {
            throw new Error("Addition failed");
        }
        addedCount.value = selected.value.length;
        emit("updated", data.value);
        selected.value = [];
        query.value = "";
    } catch {
        addError.value = t("teamPage.addError");
    } finally {
        pending.value = null;
        await nextTick();
        addFeedback.value?.scrollIntoView({ block: "nearest", behavior: "instant" });
    }
}
watch(open, () => {
    selected.value = [];
    candidate.value = null;
    query.value = "";
    addedCount.value = 0;
    addError.value = "";
    view.value = { kind: "members" };
    error.value = "";
    successMessage.value = "";
});
function memberActions(member: TeamPerspectiveMember): DropdownMenuSection[] {
    const items: DropdownMenuItem[] = [];
    if (member.make_lead_url) {
        items.push({ id: "make-lead", type: "button", label: t("teamPage.makeLead.action") });
    }
    if (member.remove_url) {
        items.push({ id: "remove", type: "button", label: t("teamPage.remove"), theme: "danger" });
    }
    return [{ id: "membership", items }];
}
function keepConfirmationFocus(event: Event) {
    if (view.value.kind !== "members") {
        event.preventDefault();
    }
}
async function selectMemberAction(member: TeamPerspectiveMember, item: DropdownMenuItem) {
    if (item.id === "remove") {
        await confirmRemove(member);
    } else if (item.id === "make-lead" && member.make_lead_url && !busy.value) {
        error.value = "";
        view.value = { kind: "confirm-make-lead", member };
        await nextTick();
        confirmationRegion.value?.focus();
    }
}
async function makeLead(member: TeamPerspectiveMember) {
    if (!member.make_lead_url || busy.value) {
        return;
    }
    pending.value = "make-lead";
    addedCount.value = 0;
    addError.value = "";
    error.value = "";
    successMessage.value = "";
    try {
        const { data, error: failure } = await useFetch(member.make_lead_url).post().json<TeamPerspectiveReport>();
        if (failure.value || !data.value) {
            throw new Error("Leadership handoff failed");
        }
        emit("updated", data.value);
        view.value = { kind: "members" };
        await nextTick();
        successMessage.value = t("teamPage.makeLead.success", { name: member.name });
    } catch {
        error.value = t("teamPage.makeLead.error");
    } finally {
        pending.value = null;
    }
    if (view.value.kind === "members") {
        await nextTick();
        doneButton.value?.$el.focus();
    }
}
async function confirmRemove(member: TeamPerspectiveMember) {
    if (!member.remove_url || busy.value) {
        return;
    }
    error.value = "";
    view.value = { kind: "confirm-remove", member };
    await nextTick();
    confirmationRegion.value?.focus();
}
function confirmAction() {
    if (view.value.kind === "confirm-remove") {
        void remove(view.value.member);
    } else if (view.value.kind === "confirm-make-lead") {
        void makeLead(view.value.member);
    } else if (view.value.kind === "confirm-delete") {
        void deleteTeam();
    }
}
async function remove(member: TeamPerspectiveMember) {
    if (!member.remove_url || busy.value) {
        return;
    }
    pending.value = "remove-member";
    addedCount.value = 0;
    addError.value = "";
    error.value = "";
    successMessage.value = "";
    try {
        const { data, error: failure } = await useFetch(member.remove_url).delete().json<TeamPerspectiveReport>();
        if (failure.value || !data.value) {
            throw new Error("Removal failed");
        }
        emit("updated", data.value);
        view.value = { kind: "members" };
        await nextTick();
        successMessage.value = t("teamPage.removed", { name: member.name });
    } catch {
        error.value = t("teamPage.removeError");
    } finally {
        pending.value = null;
    }
    if (view.value.kind === "members") {
        await nextTick();
        doneButton.value?.$el.focus();
    }
}
async function showMembers() {
    if (busy.value) {
        return;
    }
    const previousView = view.value;
    error.value = "";
    view.value = { kind: "members" };
    await nextTick();
    if (previousView.kind === "confirm-remove" || previousView.kind === "confirm-make-lead") {
        const trigger = memberButtons.value?.find((button) => button.$el.dataset.memberId === previousView.member.id);
        (trigger ?? doneButton.value)?.$el.focus();
    } else {
        deleteButton.value?.$el.focus();
    }
}
async function confirmDelete() {
    error.value = "";
    view.value = { kind: "confirm-delete" };
    await nextTick();
    confirmationRegion.value?.focus();
}
async function deleteTeam() {
    if (!deleteUrl || !report.can_manage || busy.value) {
        return;
    }
    pending.value = "delete-team";
    addedCount.value = 0;
    addError.value = "";
    error.value = "";
    try {
        const { error: failure, statusCode } = await useFetch(deleteUrl).delete();
        if (failure.value || statusCode.value !== 204) {
            throw new Error("Deletion failed");
        }
        open.value = false;
        emit("deleted");
    } catch {
        error.value = t("teamPage.delete.error");
    } finally {
        pending.value = null;
    }
}
</script>
